import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Flame } from "lucide-react";
import Header from "components/Header";
import { Button } from "components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/ui/card";
import {
  useGetLeaderboardStreakSettingsQuery,
  useUpdateLeaderboardStreakSettingsMutation,
} from "state/api";

const emptyMilestone = () => ({
  days: 7,
  rewardAmount: 0,
  rewardLabel: "",
  badgeId: "",
  badgeTitle: "",
  badgeIcon: "🏅",
});

const LeaderboardStreakSettings = () => {
  const { data, isLoading, isError } = useGetLeaderboardStreakSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateLeaderboardStreakSettingsMutation();
  const [minDailyEntries, setMinDailyEntries] = useState(1);
  const [milestones, setMilestones] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    if (!data) return;
    setMinDailyEntries(data.minDailyEntries ?? 1);
    setMilestones(
      (data.milestones || []).map((m) => ({
        days: m.days ?? 1,
        rewardAmount: m.rewardAmount ?? 0,
        rewardLabel: m.rewardLabel ?? "",
        badgeId: m.badgeId ?? "",
        badgeTitle: m.badgeTitle ?? "",
        badgeIcon: m.badgeIcon ?? "🏅",
      }))
    );
  }, [data]);

  const updateMilestone = (index, field, value) => {
    setMilestones((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [field]: value } : m))
    );
  };

  const addMilestone = () => {
    setMilestones((prev) => [...prev, emptyMilestone()]);
  };

  const removeMilestone = (index) => {
    setMilestones((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setMessage({ type: "", text: "" });
    const entries = Math.min(100, Math.max(1, Number(minDailyEntries) || 1));
    const payload = {
      minDailyEntries: entries,
      milestones: milestones.map((m) => ({
        days: Math.max(1, Number(m.days) || 1),
        rewardAmount: Math.max(0, Number(m.rewardAmount) || 0),
        rewardLabel: String(m.rewardLabel || "").trim() || `Day ${m.days}`,
        badgeId: String(m.badgeId || "").trim() || undefined,
        badgeTitle: String(m.badgeTitle || "").trim() || undefined,
        badgeIcon: String(m.badgeIcon || "").trim() || undefined,
      })),
    };
    try {
      await updateSettings(payload).unwrap();
      setMessage({
        type: "success",
        text: "Leaderboard streak settings saved.",
      });
    } catch (error) {
      setMessage({
        type: "error",
        text: error?.data?.message || "Failed to save settings.",
      });
    }
  };

  return (
    <div className="p-4 md:p-6">
      <Header
        title="Leaderboard streak"
        subtitle="Daily entries required and milestone rewards (cash + badge)"
      />

      {message.text && (
        <p
          className={`mt-4 text-sm ${
            message.type === "success" ? "text-green-600" : "text-red-600"
          }`}
        >
          {message.text}
        </p>
      )}

      <Card className="mt-6 max-w-4xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Flame className="h-5 w-5" />
            Streak rules
          </CardTitle>
          <CardDescription>
            A day counts toward streak only when the user plays at least this
            many entries that day (IST). Range 1–100.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </div>
          ) : isError ? (
            <p className="text-sm text-red-600">Failed to load settings.</p>
          ) : (
            <>
              <label className="block max-w-xs">
                <span className="text-sm font-medium">
                  Min entries per day
                </span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={minDailyEntries}
                  onChange={(e) => setMinDailyEntries(e.target.value)}
                  className="mt-1 w-full rounded-md border px-3 py-2 text-sm"
                />
              </label>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Milestones</h3>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addMilestone}
                  >
                    <Plus className="mr-1 h-4 w-4" />
                    Add milestone
                  </Button>
                </div>

                {milestones.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No milestones. Add one to reward users.
                  </p>
                )}

                {milestones.map((m, index) => (
                  <div
                    key={index}
                    className="grid gap-3 rounded-lg border p-3 md:grid-cols-3"
                  >
                    <label className="text-xs">
                      Days
                      <input
                        type="number"
                        min={1}
                        value={m.days}
                        onChange={(e) =>
                          updateMilestone(index, "days", e.target.value)
                        }
                        className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs">
                      Cash reward (₹)
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={m.rewardAmount}
                        onChange={(e) =>
                          updateMilestone(index, "rewardAmount", e.target.value)
                        }
                        className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs">
                      Reward label
                      <input
                        type="text"
                        value={m.rewardLabel}
                        onChange={(e) =>
                          updateMilestone(index, "rewardLabel", e.target.value)
                        }
                        placeholder="₹50"
                        className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs">
                      Badge id
                      <input
                        type="text"
                        value={m.badgeId}
                        onChange={(e) =>
                          updateMilestone(index, "badgeId", e.target.value)
                        }
                        placeholder="streak_30"
                        className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <label className="text-xs">
                      Badge title
                      <input
                        type="text"
                        value={m.badgeTitle}
                        onChange={(e) =>
                          updateMilestone(index, "badgeTitle", e.target.value)
                        }
                        className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                      />
                    </label>
                    <div className="flex items-end gap-2">
                      <label className="flex-1 text-xs">
                        Badge icon
                        <input
                          type="text"
                          value={m.badgeIcon}
                          onChange={(e) =>
                            updateMilestone(index, "badgeIcon", e.target.value)
                          }
                          className="mt-1 w-full rounded-md border px-2 py-1.5 text-sm"
                        />
                      </label>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => removeMilestone(index)}
                        title="Remove"
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              <Button onClick={handleSave} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save streak settings"
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LeaderboardStreakSettings;
